import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { jwtDecode } from 'jwt-decode'
import { supabase } from '../lib/supabase'
import { UserRole } from '../types/database'

interface UserInfo {
  name: string
  firstName: string | null
  lastName: string | null
}

interface JWTPayload {
  user_role?: string
  user_roles?: string[]
  [key: string]: unknown
}

interface AuthContextType {
  user: User | null
  session: Session | null
  role: UserRole | 'guest'
  roles: UserRole[]
  loading: boolean
  userInfo: UserInfo | null
  needsPasswordSetup: boolean
  invitationError: string | null
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
  clearPasswordSetup: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Get user's roles from JWT token (returns array)
 */
function getUserRoles(session: Session | null): UserRole[] {
  if (!session) return []

  try {
    const jwt = jwtDecode<JWTPayload>(session.access_token)

    // Try new user_roles array first
    if (jwt.user_roles && Array.isArray(jwt.user_roles)) {
      return jwt.user_roles as UserRole[]
    }

    // Fallback to single user_role for backward compatibility
    if (jwt.user_role) {
      return [jwt.user_role as UserRole]
    }

    return []
  } catch (error) {
    console.error('Failed to decode JWT:', error)
    return []
  }
}

/**
 * Get primary role for display purposes
 * Priority: admin > moderator > user > guest
 */
function getPrimaryRole(session: Session | null): UserRole | 'guest' {
  const roles = getUserRoles(session)

  if (roles.includes('admin')) return 'admin'
  if (roles.includes('moderator')) return 'moderator'
  if (roles.includes('user')) return 'user'

  return 'guest'
}

/**
 * Get role display name in Korean
 */
export function getRoleDisplayName(role: UserRole | 'guest'): string {
  switch (role) {
    case 'admin':
      return '관리자'
    case 'moderator':
      return '운영자'
    case 'user':
      return '사용자'
    case 'guest':
      return '게스트'
    default:
      return '알 수 없음'
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false)
  const [invitationError, setInvitationError] = useState<string | null>(null)

  /**
   * Fetch user profile from profiles table
   */
  const fetchUserProfile = async (userId: string): Promise<UserInfo | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', userId)
        .single()

      if (error) {
        // Profile might not exist yet, use email as fallback
        console.warn('Profile not found, using fallback:', error.message)
        return null
      }

      const firstName = data?.first_name || ''
      const lastName = data?.last_name || ''
      const name = [lastName, firstName].filter(Boolean).join(' ') || 'Unknown User'

      return {
        name,
        firstName: data?.first_name || null,
        lastName: data?.last_name || null,
      }
    } catch (err) {
      console.error('Error fetching user profile:', err)
      return null
    }
  }

  /**
   * Refresh the current session to get updated JWT claims
   */
  const refreshSession = async () => {
    const { data, error } = await supabase.auth.refreshSession()
    if (error) {
      console.error('Failed to refresh session:', error)
      return
    }
    if (data.session) {
      setSession(data.session)
      setUser(data.user)
    }
  }

  // 초기 해시 체크 (Supabase SDK가 소비하기 전에)
  useEffect(() => {
    const hash = window.location.hash
    console.log('🔍 Initial hash check:', hash)

    // 에러 체크
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1))
      const errorCode = params.get('error_code')
      const errorDescription = params.get('error_description')

      if (errorCode === 'otp_expired') {
        setInvitationError('초대 링크가 만료되었습니다. 관리자에게 새로운 초대를 요청하세요.')
      } else {
        setInvitationError(errorDescription || '초대 링크가 유효하지 않습니다.')
      }
      console.log('❌ Invitation error:', errorCode, errorDescription)
      return
    }

    // 초대 또는 복구 링크 체크
    if (hash.includes('type=invite') || hash.includes('type=recovery')) {
      console.log('🔑 Password setup required (from hash)')
      setNeedsPasswordSetup(true)
    }
  }, [])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔄 Initial session check:', session ? 'Found' : 'None')
      setSession(session)
      setUser(session?.user ?? null)

      // Fetch user profile if logged in (non-blocking)
      if (session?.user) {
        fetchUserProfile(session.user.id).then(profile => {
          console.log('👤 Profile loaded:', profile)
          setUserInfo(profile)
        })
      } else {
        setUserInfo(null)
      }

      console.log('✅ Initial loading complete')
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔔 Auth 상태 변경:', event, 'Session:', session ? '있음' : '없음')

      // PASSWORD_RECOVERY 이벤트 감지
      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔑 비밀번호 설정 필요: PASSWORD_RECOVERY')
        setNeedsPasswordSetup(true)
      }

      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        const roles = getUserRoles(session)
        const role = getPrimaryRole(session)
        console.log('✅ 로그인 성공, Roles:', roles, 'Primary Role:', role)

        // Fetch user profile (non-blocking)
        fetchUserProfile(session.user.id).then(profile => {
          console.log('👤 Profile loaded after auth change:', profile)
          setUserInfo(profile)
        })
      } else {
        setUserInfo(null)
      }

      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) throw error

    if (data.user && data.session) {
      // Set states immediately
      setUser(data.user)
      setSession(data.session)

      // Fetch user profile (non-blocking)
      fetchUserProfile(data.user.id).then(profile => {
        console.log('👤 Profile loaded after signIn:', profile)
        setUserInfo(profile)
      })

      // Log roles for debugging
      const detectedRoles = getUserRoles(data.session)
      const detectedRole = getPrimaryRole(data.session)

      console.log('=== 로그인 성공 ===')
      console.log('사용자 역할들:', detectedRoles)
      console.log('주 역할:', detectedRole)

      // Info message for guest users
      if (detectedRole === 'guest') {
        console.info('ℹ️ 현재 계정은 Guest 권한입니다. 읽기만 가능합니다.')
      }
    }
  }

  const signOut = async () => {
    try {
      // Use scope: 'global' to clear session from all tabs
      await supabase.auth.signOut({ scope: 'global' })
    } catch (error) {
      // Log error but don't throw - we want to sign out anyway
      console.warn('Sign out error (ignored):', error)

      // If signOut fails, manually clear storage as fallback
      try {
        localStorage.removeItem('sb-jltbxaqadamrvygjqhej-auth-token')
      } catch (e) {
        console.warn('Failed to clear local storage:', e)
      }
    } finally {
      // Always clear local state regardless of API result
      setUser(null)
      setSession(null)
      setUserInfo(null)
    }
  }

  const clearPasswordSetup = () => {
    console.log('✅ 비밀번호 설정 완료 - 플래그 해제')
    setNeedsPasswordSetup(false)
  }

  // Get role and roles from JWT claims (set by custom_access_token_hook)
  const role = getPrimaryRole(session)
  const roles = getUserRoles(session)

  const value = {
    user,
    session,
    role,
    roles,
    loading,
    userInfo,
    needsPasswordSetup,
    invitationError,
    signIn,
    signOut,
    refreshSession,
    clearPasswordSetup,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
